/**
 * Client-side storage for newsletter subscription status.
 * Used to recognize return subscribers and avoid asking for email again.
 */

const STORAGE_KEY = 'nrwh_subscriber';

interface SubscriberToken {
  subscribedAt: number;
  email?: string;
  firstName?: string;
}

export interface SubscriberInfo {
  email?: string;
  firstName?: string;
}

/**
 * Marks the current browser as having a successful subscription.
 * Call this when a user successfully subscribes via any newsletter form.
 * Email and firstName are optional; store them when available for personalization.
 */
export function markSubscribed(info?: { email?: string; firstName?: string }): void {
  try {
    if (typeof window === 'undefined') return;
    const token: SubscriberToken = {
      subscribedAt: Date.now(),
      email: info?.email?.trim() || undefined,
      firstName: info?.firstName?.trim() || undefined,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(token));
    try {
      window.dispatchEvent(new CustomEvent('nrwh_subscriber_updated'));
    } catch { /* no-op if dispatch fails */ }
  } catch {
    // localStorage may be disabled (private browsing, etc.)
  }
}

function getSubscriberToken(): SubscriberToken | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const token = JSON.parse(raw) as SubscriberToken;
    return typeof token?.subscribedAt === 'number' ? token : null;
  } catch {
    return null;
  }
}

/**
 * Returns stored subscriber info (email, firstName) if available.
 */
export function getSubscriberInfo(): SubscriberInfo | null {
  const token = getSubscriberToken();
  if (!token || (!token.email && !token.firstName)) return null;
  return {
    email: token.email,
    firstName: token.firstName,
  };
}

/**
 * Returns true if this browser has previously had a successful subscription.
 */
export function hasSubscribed(): boolean {
  // return true;
  return getSubscriberToken() !== null;
}
